import { ref, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import { useRoute } from 'vue-router'
import type {
  RouteLocationMatched,
  RouteLocationNormalizedLoaded,
} from 'vue-router'
import { RudderAnalytics } from '@rudderstack/analytics-js'
import { equalsGuard, oneOfGuard } from 'pure-parse'
import { contentPlannerContext } from '@/constants/tracking'
import { useSpace } from '@/store/modules/App/space'
import { initPostHog } from '@/store/modules/App/tracking/initPostHog'
import { parseEnv } from '@/store/modules/App/tracking/parseEnv'
import { useUser } from '@/store/modules/App/user'
import { useOrganizationStore } from '@/store/modules/Organization'
import type { User } from '@/types'
import { isEmptyObject, isNull, isString, isUndefined } from '@utils/checkers'
import { getVariant } from '@/utils/setup-application/createUnleash'
import { demoFlow } from '@/utils/setup-application/featureFlags'
import { initGtagConsent } from './gtag'
import { authRouteName } from '@/router/routes/auth'
import { onboardingRouteName } from '@/router/routes/external'
import { partnerRouteName } from '@/router/routes/partner'

/**
 * Determines whether the user, space, and organization settings allows tracking.
 * @param allowedBy
 */
export const hasConsentInSettings = (allowedBy: {
  urlQuery: boolean
  user: boolean | undefined
  space: boolean | undefined
  org: boolean | undefined
}): boolean => {
  const { urlQuery, user, space, org } = allowedBy
  if (isUndefined(org) && isUndefined(space) && isUndefined(user)) {
    // The user is not signed in: fall back to the URL query
    return urlQuery
  }
  if (!user) {
    // Users can always opt out, even if the space/org allows tracking
    return false
  }
  if (!isUndefined(org)) {
    // If the user belongs to an org, it requires consent from the org
    return org
  }
  if (!isUndefined(space)) {
    // If the user is navigating in a space, and an org is not defined, it requires consent from the space
    return space
  }
  // If the user is not in an org or space, it requires consent from the user
  return true
}

export const consents = (
  hasConsent: boolean,
  trackWithWebsite: boolean,
): Consent[] => {
  if (hasConsent && trackWithWebsite) {
    return ['website:marketing', 'required']
  } else {
    return ['required']
  }
}

const emptyToUndefined = <T>(
  value: T | Record<string, never>,
): T | undefined => (isEmptyObject(value) ? undefined : value)

const consentQueryParamName = 'cs'

const allConsents = [
  // All events that are tracked regardless of the user's consent
  'required',
  // All events that are tracked for marketing purposes and that are routed to the same destinations
  //  as the events (storyblok.com)
  'website:marketing',
] as const
type Consent = (typeof allConsents)[number]

const lastMatched = (
  // `undefined`, because the route might not be mocked in unit tests
  route: RouteLocationNormalizedLoaded | undefined,
): RouteLocationMatched | undefined =>
  // Assert to union with `undefined` since indexing might return `undefined` and we haven't enabled `noUncheckedIndexedAccess` in tsconfig.json
  route?.matched?.[route.matched?.length - 1]

const websiteTrackedPaths = [
  authRouteName.login,
  authRouteName.signup,
  partnerRouteName.partnerRegistration,
  partnerRouteName.partnerSignupConfirmation,
  onboardingRouteName,
] as const

const isTrackedPath = oneOfGuard(
  ...websiteTrackedPaths.map((name) => equalsGuard(name)),
)

/**
 * Important: must not be able to throw an error, as it is called in the store initialization.
 */
const initRudderStack = (): RudderAnalytics | undefined => {
  try {
    const writeKey = parseEnv(import.meta.env.VITE_RUDDERSTACK_WRITE_KEY)
    const dataPlaneUrl = parseEnv(
      import.meta.env.VITE_RUDDERSTACK_DATA_PLANE_URL,
    )
    const analytics = new RudderAnalytics()
    if (writeKey && dataPlaneUrl) {
      analytics.load(writeKey, dataPlaneUrl, {
        logLevel: 'NONE',
        integrations: {
          All: true,
        },
        consentManagement: {
          enabled: true,
          provider: 'custom', // important to use the literal value 'custom' here
        },
        preConsent: {
          enabled: true,
          storage: {
            strategy: 'none', // no cookies to be stored. Fully anonymous.
          },
        },
      })
    }
    return analytics
  } catch (error) {
    console.error('Failed to initialize RudderStack analytics', error)
    return undefined
  }
}

export const useTracking = defineStore('appTrackingNew', () => {
  const route = useRoute()
  const user = useUser()
  const space = useSpace()
  const org = useOrganizationStore()
  const featureVariant = ref<string>()

  const hasConsent = computed(() => {
    const consentQuery = route?.query?.[consentQueryParamName]
    const allowedByQuery = !(isUndefined(consentQuery) || isNull(consentQuery))

    return hasConsentInSettings({
      urlQuery: allowedByQuery,
      org: emptyToUndefined(org.org)?.track_statistics,
      space: emptyToUndefined(space.space)?.track_statistics,
      user: emptyToUndefined(user.user)?.track_statistics,
    })
  })

  const analytics = initRudderStack()
  const postHog = initPostHog()
  // eslint-disable-next-line vue/no-ref-object-reactivity-loss
  const { updateGtagConsent } = initGtagConsent(hasConsent.value)

  const trackAsWebsite = computed<boolean>(
    () => isString(route?.name) && isTrackedPath(route.name),
  )

  const updateConsent = (hasConsent: boolean): void => {
    // Update Google Analytics 4 consent
    updateGtagConsent(hasConsent)

    // Update RudderStack consent
    const consentIds = consents(hasConsent, trackAsWebsite.value)
    const deniedIds = allConsents.filter(
      (consent) => !consentIds.includes(consent),
    )

    analytics?.consent({
      consentManagement: {
        allowedConsentIds: consentIds,
        deniedConsentIds: deniedIds,
      },
    })
  }

  const getSpacePlanForEvent = (plan: string | undefined, is_demo = false) => {
    if (!plan) {
      return
    }

    const planWithoutSuffix = plan
      .replace(/(?:_2025|_1_3_month_usd|_1_6_year_usd)$/, '')
      .toLowerCase()

    return planWithoutSuffix === 'starter' && is_demo
      ? 'demo'
      : planWithoutSuffix
  }

  const analyticsTrack = ({
    event,
    payload: properties = {},
  }: {
    event: string
    // It would be possible to allow arrays and objects too; we can add this later if we want to allow this.
    payload: {
      context?:
        | typeof contentPlannerContext
        | 'onboarding'
        | 'quickstart'
        | 'dashboard'
        | 'content'
        | 'component_library'
        | 'asset_library'
        | 'visual_editor'
        | 'sidebar'
        | 'idea'
        | 'rich_text'
        | 'settings'
        | 'labs'
        | 'concept_room'
        | 'blueprint'
        | 'new_space'
        | 'demo'
        | 'custom_role'
        | 'field_settings'
        | 'joyride'
      link_text?: string
      space_id?: number
      space_plan?: string
      is_demo_space?: boolean
    } & Record<string, unknown>
  }): void => {
    /**
     * Remove reserved properties from the payload.
     * This is not documented in their SDK, but on their website: https://www.rudderstack.com/docs/event-spec/standard-events/track/#properties
     */
    const {
      revenue: _1,
      currency: _2,
      value: _3,
      ...nonReservedProperties
    } = properties
    updateConsent(hasConsent.value)

    const { space_id, space_plan, is_demo_space } = nonReservedProperties
    const plan = space?.space?.plan || space_plan
    const isDemo = space?.space?.is_demo || is_demo_space

    const eventProperties = {
      ...nonReservedProperties,
      with_space_id: space?.space?.id || space_id,
      with_space_plan: getSpacePlanForEvent(plan, isDemo),
      // If variant is empty don't send it through posthog.
      ...(featureVariant.value ? { variant: featureVariant.value } : {}),
    }

    if (trackAsWebsite.value) {
      // Only send events to RudderStack if they concern the website (login and signup pages)
      analytics?.track(event, eventProperties)
    }
    postHog.capture(event, eventProperties)
  }

  const pageTrack = (payload: {
    url: string
    path: string
    title: string
  }): void => {
    updateConsent(hasConsent.value)
    if (trackAsWebsite.value) {
      // Only send events to RudderStack if they concern the website (login and signup pages)
      analytics?.page(payload.title, payload)
    }
    // $pageView is a reserved event in PostHog
    postHog.capture('$pageView', {
      ...payload,
      route_name: payload.title,
    })
  }

  watch(
    [
      (): string | undefined => lastMatched(route)?.path,
      (): symbol | string | undefined => lastMatched(route)?.name,
    ],
    async ([path, name]) => {
      if (!isString(path) || !isString(name)) {
        return
      }

      const variant = await getVariant(demoFlow)
      featureVariant.value = variant?.name

      pageTrack({
        url: `${location.host}/#${path}`,
        path: path,
        title: name,
        // If variant is empty don't send it through posthog.
        ...(featureVariant.value ? { variant: featureVariant.value } : {}),
      })
    },
    {
      // Track the very first page view
      immediate: true,
    },
  )

  // Gtag identify
  watch(
    [(): boolean => hasConsent.value],
    ([hasConsent]) => {
      updateConsent(hasConsent)
    },
    {
      immediate: true,
    },
  )

  // RudderStack identify
  watch(
    [(): boolean => trackAsWebsite.value],
    ([trackAsWebsite]) => {
      if (trackAsWebsite) {
        // Only send events to RudderStack if they concern the website (login and signup pages)
        analytics?.identify(undefined)
      }
    },
    {
      immediate: true,
    },
  )

  // PostHog identify
  watch(
    [
      // The app seems to sometimes reload the user out of nowhere. Since the new user object has a different reference,
      //  the effect will run again if we watch the entire user object. The solution is to only watch the user id and
      //  name, which are primitive values.
      (): string | undefined => user.user?.id?.toString(10),
      (): string | undefined => user.user?.friendly_name,
      (): User['sso'] => user.user?.sso,
      (): string | undefined => user.user?.email,
      (): string | undefined => user.user?.alt_email,
      (): string | undefined => user.user?.company_name ?? undefined,
      (): string | undefined => user.user?.org_role ?? undefined,
      (): string | undefined => user.user?.job_role ?? undefined,
      (): string | undefined => user.userEnrichedInfo?.title ?? undefined,
      (): string | undefined => user.user?.ui_theme?.theme,
    ],
    ([
      userId,
      name,
      isSso,
      email,
      altEmail,
      companyName,
      orgRole,
      jobRole,
      jobTitle,
      theme,
    ]) => {
      const postHogSessionUserId = postHog.persistence?.props.$user_id
      const token = localStorage.getItem('token')

      if (userId && postHogSessionUserId !== userId) {
        postHog.identify(
          userId, // Required. This should be the user ID from Storyblok
          {
            // Must be `alt_email` if the user is an SSO one, because the email field
            // in that case will be the ugly SSO email address
            email: isSso ? altEmail : email,
            name: name,
            company: companyName,
            org_role: orgRole,
            job_role: jobRole,
            job_title: jobTitle,
            theme: theme,
          },
        )
      } else if (!token && postHogSessionUserId) {
        // Reset when logged out: https://posthog.com/docs/libraries/js
        postHog.reset()
      }
    },
    {
      immediate: true,
    },
  )

  return {
    analyticsTrack,
  }
})
